import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import ReactFlow, {
  ReactFlowProvider,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  Background
} from 'reactflow';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import 'reactflow/dist/style.css'; // استایل‌های پایه React Flow
import CustomNode from './CustomNode'; // گره سفارشی برای صفحات
import Sidebar from './Sidebar'; // کامپوننت سایدبار
import SidebarForms from './SidebarForms'; // فرم‌های داخل سایدبار
import BacklinkToolModal from './BacklinkToolModal'; // مودال ابزار نقشه بک‌لینک
import BacklinkMapView from './BacklinkMapView'; // نمایش نقشه بک‌لینک

// تعریف انواع گره‌های سفارشی
const nodeTypes = { custom: CustomNode };

// کامپوننت داخلی برای دکمه خروجی PDF نقشه اصلی
const MainMapExportButton = () => {
  const [loading, setLoading] = useState(false);

  const onClick = () => {
    setLoading(true);
    const mapContainer = document.querySelector('#main-mind-map-viewport');
    if (!mapContainer) {
      setLoading(false);
      alert("خطا: عنصر نقشه یافت نشد.");
      return;
    }

    // تاخیر کوتاه برای اطمینان از رندر کامل خطوط SVG
    setTimeout(() => {
      html2canvas(mapContainer, {
        useCORS: true,
        allowTaint: true,
        scale: 1.5, // مقیاس برای کیفیت بهتر (قابل تنظیم)
        backgroundColor: '#f7fafc', // رنگ پس‌زمینه سفید نقشه
      }).then((canvas) => {
        const imgData = canvas.toDataURL('image/jpeg', 0.8); // فرمت JPEG با فشرده‌سازی
        const pdf = new jsPDF({
          orientation: 'landscape', // افقی
          unit: 'px', // واحد پیکسل بر اساس اندازه canvas
          format: [canvas.width, canvas.height] // اندازه PDF بر اساس اندازه canvas
        });
        pdf.addImage(imgData, 'JPEG', 0, 0, canvas.width, canvas.height, undefined, 'FAST'); // فشرده‌سازی سریع
        pdf.save('main-mind-map.pdf'); // نام فایل خروجی
        setLoading(false);
      }).catch((err) => {
        console.error("PDF Export Error:", err);
        alert("خطا در ایجاد خروجی PDF.");
        setLoading(false);
      });
    }, 100); // تاخیر 100 میلی‌ثانیه
  };
  return <button onClick={onClick} className="export-btn-main" disabled={loading}>{loading ? 'در حال ساخت...' : 'خروجی PDF'}</button>;
};


// کامپوننت اصلی نقشه ذهنی
const MindMapCanvas = () => {
  // State های اصلی
  const [nodes, setNodes, onNodesChange] = useNodesState([]); // صفحات (گره‌ها)
  const [edges, setEdges, onEdgesChange] = useEdgesState([]); // اتصالات
  const [isSidebarOpen, setSidebarOpen] = useState(false); // وضعیت باز بودن سایدبار
  const [sidebarContent, setSidebarContent] = useState({ title: '' }); // عنوان سایدبار
  const [selectedPage, setSelectedPage] = useState(null); // صفحه‌ای که برای افزودن فرزند انتخاب شده
  const [activePage, setActivePage] = useState(null); // صفحه‌ای که در سایدبار نمایش داده می‌شود
  const [activeMenuType, setActiveMenuType] = useState(''); // نوع بخش فعال در سایدبار (keywords, links, ...)
  const [isToolModalOpen, setToolModalOpen] = useState(false); // وضعیت مودال ابزار بک‌لینک
  const [isMapViewOpen, setMapViewOpen] = useState(false); // وضعیت نمایش نقشه بک‌لینک
  const [pageIdsForMap, setPageIdsForMap] = useState([]); // ID صفحاتی که در نقشه بک‌لینک نمایش داده می‌شوند

  // Ref برای مدیریت دیبانس ذخیره داده‌های سایدبار
  const dataDebounceRef = useRef(null);

  // ۱. بارگذاری اولیه داده‌ها از سرور
  useEffect(() => {
    const apiUrl = 'https://sseo.afaq.lc/seo_architect_api/get_map_data.php'; // آدرس API شما
    const token = localStorage.getItem('authToken');
    if (!token) {
      // اگر توکن نبود، کاربر لاگ اوت شده و باید به صفحه لاگین هدایت شود
      window.location.reload();
      return;
    }

    fetch(apiUrl, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(response => {
        if (response.status === 401) {
          localStorage.clear(); // پاک کردن توکن‌های نامعتبر
          window.location.reload();
          throw new Error('توکن نامعتبر یا منقضی شده.');
        }
        if (!response.ok) throw new Error('خطا در دریافت اطلاعات نقشه.');
        return response.json();
      })
      .then(data => {
        if (data && data.nodes && data.edges) {
          setNodes(data.nodes); // تنظیم صفحات دریافتی
          setEdges(data.edges); // تنظیم اتصالات دریافتی
        }
      })
      .catch(error => console.error("Error fetching map data:", error));
  }, [setNodes, setEdges]); // [] یعنی این افکت فقط یک بار در زمان Mount اجرا می‌شود

  // ۲. تابع برای باز کردن سایدبار هنگام کلیک روی آیکون‌ها
  const handleMenuClick = useCallback((page, menuType) => {
    const titles = { keywords: '💎 تحقیق کلمات کلیدی', competitors: '📊 تحلیل رقبا', content: '📝 استراتژی محتوا', links: '🔗 لینک‌سازی' };
    setSidebarContent({ title: titles[menuType] });
    setActivePage(page); // صفحه فعال برای سایدبار
    setActiveMenuType(menuType); // بخش فعال در سایدبار
    setSidebarOpen(true); // باز کردن سایدبار
  }, []);

  // ۳. تابع اصلی برای ذخیره خودکار داده‌های سایدبار (با دیبانس)
  const updateNodeData = useCallback((pageId, dataKey, newData) => {
    // آپدیت فوری UI (خوشبینانه)
    setNodes(nds => nds.map(node => {
      if (node.id === pageId) {
        const updatedNode = { ...node, data: { ...node.data, [dataKey]: newData } };
        // اگر سایدبار باز بود و مربوط به همین صفحه بود، state آن را هم آپدیت کن
        if (activePage && activePage.id === pageId) setActivePage(updatedNode);
        return updatedNode;
      }
      return node;
    }));

    // نگاشت کلید داده به نام فایل API مربوطه
    const API_ENDPOINTS = {
      keywords: 'update_keywords.php',
      competitors: 'update_competitors.php',
      content: 'update_content.php',
      links: 'update_links.php'
    };
    const endpoint = API_ENDPOINTS[dataKey];

    // فقط اگر API مربوطه وجود داشت، ذخیره‌سازی را انجام بده
    if (endpoint) {
      if (dataDebounceRef.current) clearTimeout(dataDebounceRef.current); // لغو تایمر قبلی
      // تنظیم تایمر جدید برای ۲ ثانیه بعد
      dataDebounceRef.current = setTimeout(() => {
        const apiUrl = `https://sseo.afaq.lc/seo_architect_api/${endpoint}`;
        const token = localStorage.getItem('authToken');
        const payload = { pageId: pageId, [dataKey]: newData };

        fetch(apiUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
          body: JSON.stringify(payload)
        })
        .then(res => res.json())
        .then(data => {
          if (!data.success) console.error(`Failed to save ${dataKey}:`, data.message);
          else console.log(`${dataKey} saved successfully!`);
        })
        .catch(err => console.error(`Network error on saving ${dataKey}:`, err));
      }, 2000); // تاخیر ۲ ثانیه‌ای
    }
  }, [activePage, setNodes]); // وابستگی‌ها

  // ۴. تابع برای تغییر نام صفحه (ذخیره فوری)
  const updatePageLabel = useCallback((pageId, newLabel) => {
    // آپدیت خوشبینانه UI
    setNodes(nds => nds.map(node => (node.id === pageId ? { ...node, data: { ...node.data, label: newLabel } } : node)));
    // ارسال به سرور
    fetch('https://sseo.afaq.lc/seo_architect_api/update_page_label.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ pageId: pageId, label: newLabel })
    }).then(res => res.json()).then(data => !data.success && alert("خطا در ذخیره نام صفحه."));
  }, [setNodes]); // وابستگی

  // ۵. تابع برای حذف صفحه (ذخیره فوری)
  const deletePage = useCallback((pageIdToDelete) => {
    // آپدیت خوشبینانه UI
    setNodes(nds => nds.filter(node => node.id !== pageIdToDelete));
    setEdges(eds => eds.filter(edge => edge.source !== pageIdToDelete && edge.target !== pageIdToDelete));
    // ارسال به سرور
    fetch('https://sseo.afaq.lc/seo_architect_api/delete_page.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify({ pageId: pageIdToDelete })
    });
  }, [setNodes, setEdges]); // وابستگی‌ها

  // ۶. تابع برای افزودن صفحه فرزند (ذخیره فوری)
  const addChildNode = useCallback(() => {
    if (!selectedPage) { alert("لطفاً ابتدا یک صفحه را انتخاب کنید!"); return; }
    // آماده‌سازی داده‌ها برای ارسال
    const newPageData = {
      selectedPageId: selectedPage.id,
      label: `صفحه جدید`,
      position: { x: selectedPage.position.x, y: selectedPage.position.y + 120 }
    };
    // ارسال به سرور
    fetch('https://sseo.afaq.lc/seo_architect_api/add_page.php', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('authToken')}` },
      body: JSON.stringify(newPageData)
    })
    .then(res => res.json())
    .then(data => {
      // اگر موفق بود، صفحه و اتصال جدید را به UI اضافه کن
      if (data.success && data.newNode && data.newEdge) {
        setNodes(nds => nds.concat(data.newNode));
        setEdges(eds => eds.concat(data.newEdge));
      } else { alert(`خطا در افزودن صفحه: ${data.message || 'پاسخ نامعتبر'}`); }
    });
  }, [selectedPage, setNodes, setEdges]); // وابستگی‌ها

  // ۷. تابع برای ذخیره موقعیت صفحه پس از اتمام کشیدن (Drag & Drop)
  const handleNodeDragStop = useCallback((event, node) => {
    const apiUrl = 'https://sseo.afaq.lc/seo_architect_api/update_page_position.php';
    const token = localStorage.getItem('authToken');
    // ارسال موقعیت جدید به سرور
    fetch(apiUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ pageId: node.id, position: node.position })
    })
    .then(res => res.json())
    .then(data => { if (!data.success) console.error("Failed to save position:", data.message); })
    .catch(err => console.error("Network error saving position:", err));
  }, []); // بدون وابستگی

  // هندلرهای استاندارد React Flow
  const onNodeClick = useCallback((event, node) => setSelectedPage(node), []);
  const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

  // ۸. محاسبه آمار کلی لینک‌سازی (بدون شمارش تکراری رپورتاژ)
  const globalLinkStats = useMemo(() => {
    let totalReportageCost = 0, totalBacklinkCost = 0;
    const countedReportageUrls = new Set();
    nodes.forEach(node => {
      node.data?.links?.forEach(link => {
        if (link.type === 'reportage') {
          if (link.link_address && !countedReportageUrls.has(link.link_address)) {
            totalReportageCost += Number(link.total_cost) || 0;
            countedReportageUrls.add(link.link_address);
          }
        } else if (link.type === 'backlink') {
          totalBacklinkCost += Number(link.cost) || 0;
        }
      });
    });
    return { totalReportageCost, totalBacklinkCost };
  }, [nodes]); // وابسته به تغییرات صفحات

  // ۹. آماده‌سازی نهایی صفحات برای ارسال به React Flow (تزریق توابع callback)
  const nodesWithFunctions = useMemo(() => nodes.map(node => ({
    ...node,
    data: { ...node.data, onMenuClick: handleMenuClick, onUpdateLabel: updatePageLabel, onDeletePage: deletePage }
  })), [nodes, handleMenuClick, updatePageLabel, deletePage]); // وابسته به تغییرات صفحات و توابع

  // View
  return (
    <div style={{ height: '100vh', width: '100%' }}>
      {/* دکمه‌های بالای صفحه */}
      <div className="main-header-buttons">
        <button onClick={addChildNode} className="add-node-btn">افزودن صفحه فرزند +</button>
        <button onClick={() => setToolModalOpen(true)} className="tools-btn">ابزارها 🛠️</button>
        <MainMapExportButton />
      </div>

      {/* کانتینر اصلی React Flow */}
      <ReactFlowProvider>
        <div id="main-mind-map-viewport" style={{ width: '100%', height: '100%' }}>
          <ReactFlow
            nodes={nodesWithFunctions} // استفاده از صفحات آماده شده
            edges={edges}
            onNodesChange={onNodesChange} // هندلر پیش‌فرض کافی است
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            nodeTypes={nodeTypes} // استفاده از گره سفارشی ما
            onNodeDragStop={handleNodeDragStop} // فعال کردن ذخیره موقعیت
            fitView // زوم خودکار در بارگذاری اولیه
          >
            <Controls />
            <Background variant="dots" gap={15} size={1} color="#e2e8f0" />
          </ReactFlow>
        </div>
      </ReactFlowProvider>
      
      {/* سایدبار */}
      <Sidebar isOpen={isSidebarOpen} onClose={() => setSidebarOpen(false)} title={sidebarContent.title}>
        <SidebarForms
          activeNode={activePage}
          menuType={activeMenuType}
          updateNodeData={updateNodeData} // ارسال تابع ذخیره‌سازی به فرم‌ها
          globalLinkStats={globalLinkStats}
        />
      </Sidebar>

      {/* مودال ابزار نقشه بک‌لینک */}
      <BacklinkToolModal
        isOpen={isToolModalOpen}
        onClose={() => setToolModalOpen(false)}
        onGenerateMap={(selectedPageIds) => {
          setPageIdsForMap(selectedPageIds);
          setMapViewOpen(true);
          setToolModalOpen(false);
        }}
      />

      {/* نمایش شرطی نقشه بک‌لینک */}
      {isMapViewOpen && (
        <BacklinkMapView 
          pageIds={pageIdsForMap}
          onClose={() => setMapViewOpen(false)}
        />
      )}
    </div>
  );
};

export default MindMapCanvas;